import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"

// Append-only LIMS audit trail (GMP: who / what / when / why). Entries are NEVER edited
// or deleted — there is no update/delete path here or in the UI.
//
// SPEC-021 (B4): the audit trail is SERVER-DERIVED. Every LIMS mutation endpoint writes its
// own immutable, server-stamped entry (actor / initials / timestamp from the authenticated
// member — never the client); there is NO POST /lims/audit and no client writeAudit any more.
// This slice only READS the trail (GET /lims/audit, newest first — the server's ordering and
// timestamps are authoritative, so no client-side sorting either).
//
// SPEC-016 (calculation engine): a calculated result value carries its raw inputs + formula +
// computed value INSIDE the result record, so they ride along with the server's
// `limsAuditResultSaved` snapshot (objectType "result") — the computed value is reproducible
// from the audited record.

// Canonical set of object types the LIMS module audits — kept complete here so the audit
// viewer's Type filter lists every category, not only those that already appear in the log.
// Mirrors the objectType values the BACKEND writes across the /lims routes. "consumable" is
// the generalised reagent register (PRD-003 Area 3); "reagent" is RETAINED so historical
// reagent-era audit entries written before the rename stay filterable. "review" is the
// review-action audit (PRD-003 Area 1). "qaDecision", "batch" and "rerun" are the
// OOS -> QA -> rerun audit categories (PRD-003 Area 4 / SPEC-010). "investigation" and "capa"
// are the structured OOS-investigation + CAPA categories (PRD-005 Area 4 / SPEC-018). "sst" is
// the System Suitability Test category (PRD-005 Area 3 / SPEC-017). "commentTemplate" is the
// comment-library catalogue (canned standard comments per signature context, PRD-008 / SPEC-033).
export const AUDIT_OBJECT_TYPES = [
  "member", "product", "consumable", "reagent", "sample", "oos",
  "result", "review", "forskrift", "qaDecision", "batch", "rerun",
  "investigation", "capa", "sst", "commentTemplate",
]

// `entries` is the GLOBAL trail (the AuditViewer screen). `byAnalysis` is the analysis-scoped
// trail keyed by analysisId (SPEC-034 F2 — the AuditSection on the result-entry panel), kept
// SEPARATE so an analysis-scoped fetch never overwrites the global trail (no AuditViewer
// regression). `byAnalysisLoading` tracks the per-analysis lazy fetch.
const initialState = { entries: [], isLoading: false, byAnalysis: {}, byAnalysisLoading: {} }

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsAudit/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsAudit/fetch":
      return { ...state, entries: action.res, isLoading: false }
    case "limsAudit/fetchForAnalysis/BEGIN": {
      // The analysisId is the thunk's first argument (asyncAction echoes it on `args`).
      const analysisId = action.args && action.args[0]
      if (!analysisId) return state
      return { ...state, byAnalysisLoading: { ...state.byAnalysisLoading, [analysisId]: true } }
    }
    case "limsAudit/fetchForAnalysis": {
      const analysisId = action.args && action.args[0]
      if (!analysisId) return state
      return {
        ...state,
        byAnalysis: { ...state.byAnalysis, [analysisId]: action.res },
        byAnalysisLoading: { ...state.byAnalysisLoading, [analysisId]: false },
      }
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsAudit/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/audit").then((res) => res.items)
})

// SPEC-034 (F2): fetch the audit trail SCOPED to one analysis run (the backend resolves the
// entry set from the analysis's own children — SPEC-017). Stored under byAnalysis[analysisId];
// the result-entry panel's AuditSection lazy-fires this on its first expand.
export const fetchForAnalysis = asyncAction("limsAudit/fetchForAnalysis", (_dispatch, analysisId) => {
  return limsRequest("GET", `/lims/audit?analysisId=${encodeURIComponent(analysisId)}`).then((res) => res.items)
})

export const selectAuditEntries = (state) => state.limsAudit.entries

// The analysis-scoped trail for one analysis (or an empty array before it has been fetched).
export const selectAuditForAnalysis = (state, analysisId) =>
  (analysisId && state.limsAudit.byAnalysis[analysisId]) || []

export const selectAuditForAnalysisLoading = (state, analysisId) =>
  !!(analysisId && state.limsAudit.byAnalysisLoading[analysisId])
